import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../store/UserGeneralInfoStore";
import { BaseAmplitudeEvent, BaseEventInput } from "../types/amplitudeEvent";
import { getEnv } from "./runtimeEnv";

/**
 * Builds the base envelope every Amplitude event must include.
 *
 * - event_time_local: human-readable timestamp in the user's IANA zone, e.g. "April 26, 2027, 14:35:09"
 * - event_time_utc:   human-readable timestamp in UTC, e.g. "April 26, 2027, 09:05:09"
 * - event_time_zone:  IANA zone string (e.g. "Asia/Kolkata", "America/New_York")
 * - app_version:      value of REACT_APP_VERSION from build env, "unknown" otherwise
 * - org_name:         caller-supplied value, otherwise the active facility name
 *                     cached in UserStore by the singleHealthFacilityDetails slice
 */
export const buildBaseEvent = (input: BaseEventInput): BaseAmplitudeEvent => {
  const now = new Date();
  const event_time_zone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const formatHumanReadable = (timeZone: string): string =>
    now.toLocaleString("en-US", {
      timeZone,
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

  const event_time_local = formatHumanReadable(event_time_zone);
  const event_time_utc = formatHumanReadable("UTC");

  const cachedOrgName =
    (UserStore.getItem(UserGeneralInfoContextKeys.ORG_NAME) as
      string | null | undefined) ?? null;

  return {
    order_id: input.order_id ?? null,
    org_id: input.org_id ?? null,
    org_name: input.org_name ?? cachedOrgName,
    event_name: input.event_name,
    user_id: input.user_id ?? null,
    user_email: input.user_email ?? null,
    event_time_local,
    event_time_utc,
    event_time_zone,
    app_version: getEnv("REACT_APP_VERSION") ?? "unknown",
  };
};
